import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiService } from '@mm-aggregator/drf-dynamics';

export interface MatchedWebhookRule {
  id: string;
  name: string;
  webhook_url: string;
  priority: number;
}

/**
 * WebhookEvent model from the backend.
 */
export interface WebhookEvent {
  id: string;
  provider_code: string;
  financial_entity: string | null;
  financial_entity_code: string | null;
  financial_entity_name: string | null;
  execution_provider: string | null;
  execution_provider_code: string | null;
  execution_provider_name: string | null;
  event_type: string;
  transaction_id: string | null;
  provider_transaction_id: string | null;
  payload: Record<string, unknown>;
  headers: Record<string, string>;
  status: string;
  status_message: string;
  forwarded_to_url: string | null;
  forwarded_at: string | null;
  forward_attempts: number;
  forward_response_code: number | null;
  forward_response_body: string;
  forward_request_body: Record<string, unknown>;
  forward_request_headers: Record<string, unknown> | null;
  application: string | null;
  application_name: string | null;
  application_client_id: string | null;
  merchant_id: string | null;
  merchant_name: string | null;
  matched_rules: MatchedWebhookRule[];
  confirmation_response: string;
  created_at: string;
  updated_at: string;
  real_transaction_id?: string;
}

/**
 * WebhookRule model from the backend.
 */
export interface WebhookRule {
  id: string;
  name: string;
  description: string;
  status: string;
  provider_code: string | null;
  event_type: string | null;
  application: string | null;
  application_name: string | null;
  application_client_id: string | null;
  webhook_url: string;
  max_retries: number;
  retry_delay_seconds: number;
  priority: number;
  filters: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

/**
 * Retry response from the backend.
 */
export interface RetryResponse {
  message: string;
  webhook_event: WebhookEvent;
}

/**
 * Service for managing Webhook Events and Rules.
 */
@Injectable({
  providedIn: 'root',
})
export class WebhookService {
  private readonly api = inject(ApiService);
  private readonly eventsPath = 'manage/webhook-events';
  private readonly rulesPath = 'manage/webhook-rules';

  // ─── Webhook Events ──────────────────────────────────────────

  /**
   * Get a single webhook event by ID.
   */
  getWebhookEvent(id: string): Observable<WebhookEvent> {
    return this.api.get<WebhookEvent>(`${this.eventsPath}/${id}/`);
  }

  /**
   * Retry a failed webhook event (full re-processing).
   */
  retryWebhook(id: string): Observable<RetryResponse> {
    return this.api.post<RetryResponse>(`${this.eventsPath}/${id}/retry/`, {});
  }

  /**
   * Resend a webhook to the merchant (forward only, no transaction re-processing).
   * Only works for FAILED or RETRYING events; cannot resend FORWARDED events.
   */
  resendWebhook(id: string): Observable<RetryResponse> {
    return this.api.post<RetryResponse>(`${this.eventsPath}/${id}/resend/`, {});
  }

  // ─── Webhook Rules ───────────────────────────────────────────

  /**
   * Get a single webhook rule by ID.
   */
  getWebhookRule(id: string): Observable<WebhookRule> {
    return this.api.get<WebhookRule>(`${this.rulesPath}/${id}/`);
  }

  /**
   * Create a webhook rule.
   */
  createRule(data: Partial<WebhookRule>): Observable<WebhookRule> {
    return this.api.post<WebhookRule>(`${this.rulesPath}/`, data);
  }

  /**
   * Update a webhook rule.
   */
  updateRule(id: string, data: Partial<WebhookRule>): Observable<WebhookRule> {
    return this.api.patch<WebhookRule>(`${this.rulesPath}/${id}/`, data);
  }

  /**
   * Delete a webhook rule.
   */
  deleteRule(id: string): Observable<void> {
    return this.api.delete(`${this.rulesPath}/${id}/`);
  }
}
